import { useCallback, useRef } from 'react';

import { AutoScroller, Cursor, Feedback, PointerSensor, PreventSelection } from '@dnd-kit/dom';
import { move } from '@dnd-kit/helpers';
import { DragDropProvider } from '@dnd-kit/react';
import { Alert, AlertDescription, AlertTitle, Button } from 'design-system';
import { CircleAlert, Database, GripVertical } from 'lucide-react';

import { EmptyState } from '@/features/dashboard/components/AppForm/AppForm/components/EmptyState';
import { useAppForm } from '@/features/dashboard/components/AppForm/AppForm/context';
import { AppType } from '@/features/dashboard/types';
import getAmountLabel from '@/shared/lib/format/getAmountLabel';

import { DroppableArea } from './DroppableArea';

import type { DragDropContainerProps } from './types';

const sensors = [
  PointerSensor.configure({
    activatorElements(source) {
      return [source.element, source.handle];
    }
  })
];

const DragDropContainer = ({
  itemsName = 'item',
  maxCapacity,
  setOpen
}: DragDropContainerProps) => {
  const { form, setCanPreview } = useAppForm();
  const container = useRef<HTMLDivElement>(null);
  const hadSymbolsBefore = useRef(false);
  const handleClearAllClick = useCallback(() => {
    form.setFieldValue('symbols', { active: [], backlog: [] });
  }, [form]);

  return (
    <form.Subscribe selector={s => ({ symbols: s.values?.symbols, type: s.values?.type })}>
      {({ symbols, type }) => {
        if (symbols && symbols.active.length > 0) {
          hadSymbolsBefore.current = true;
        }

        let widgetType: string;
        switch (type) {
          case AppType.Tiles:
            widgetType = 'highlighted price';
            break;
          case AppType.Table:
            widgetType = 'data table';
            break;
          default:
            widgetType = 'chart';
        }

        if (!symbols) return null;

        const isMaxCapacityReached =
          symbols.backlog.length > 0 || symbols.active.length > maxCapacity;

        return (
          <DragDropProvider
            plugins={[Feedback, AutoScroller, Cursor, PreventSelection]}
            sensors={sensors}
            onDragEnd={() => {
              setCanPreview(true);
              const currentSymbols = form.state.values?.symbols;
              if (!currentSymbols) return;

              const { active, backlog } = currentSymbols;

              if (active.length > maxCapacity) {
                const itemsToRemove = active.slice(maxCapacity);
                const newActive = active.slice(0, maxCapacity);

                form.setFieldValue('symbols', {
                  active: newActive,
                  backlog: [...itemsToRemove, ...backlog]
                });
              } else {
                const itemsToAdd = backlog.slice(0, maxCapacity - active.length);
                const newBacklog = backlog.slice(maxCapacity - active.length);
                form.setFieldValue('symbols', {
                  active: [...active, ...itemsToAdd],
                  backlog: newBacklog
                });
              }
            }}
            onDragOver={event => {
              const currentSymbols = form.state.values?.symbols;
              if (!currentSymbols) return;

              form.setFieldValue('symbols', move(currentSymbols, event));
            }}
            onDragStart={() => {
              setCanPreview(false);
            }}
          >
            <div>
              {symbols.active.length > 0 && (
                <>
                  <div className='mb-4 flex items-center justify-between text-sm'>
                    <span>
                      Selected: {symbols.active.length + symbols.backlog.length}
                      {symbols.backlog.length > 0 &&
                        ` (${symbols.active.length} displayed, ${symbols.backlog.length} not displayed)`}
                    </span>
                    <Button
                      size='sm'
                      variant='ghost'
                      onClick={handleClearAllClick}
                    >
                      Clear all
                    </Button>
                  </div>
                  {isMaxCapacityReached && (
                    <Alert className='mb-6'>
                      <CircleAlert aria-hidden='true' />
                      <AlertTitle>
                        App display limit: {getAmountLabel(maxCapacity, itemsName)}
                      </AlertTitle>
                      <AlertDescription>
                        Only the first {getAmountLabel(maxCapacity, itemsName)} will be displayed on
                        the {widgetType}. Items beyond this limit can be reordered by dragging them
                        to higher positions.
                      </AlertDescription>
                    </Alert>
                  )}
                </>
              )}

              {symbols.active.length === 0 && (
                <>
                  <p className='mb-4 text-sm'>Selected: 0</p>
                  {hadSymbolsBefore.current && (
                    <Alert
                      className='mb-6'
                      variant='destructive'
                    >
                      <CircleAlert aria-hidden='true' />
                      <AlertTitle>No {itemsName} selected</AlertTitle>
                      <AlertDescription>
                        You need to add at least one {itemsName} to display this app.
                      </AlertDescription>
                    </Alert>
                  )}
                  <EmptyState
                    description={`Browse the catalog to select ${itemsName}s`}
                    icon={<Database />}
                    setOpen={setOpen}
                    title={`No ${itemsName}s selected`}
                  />
                </>
              )}
              <div ref={container}>
                {symbols.active.length > 0 && (
                  <>
                    <div className='grid grid-cols-[100px_100px_minmax(0,1fr)] items-center text-sm font-medium'>
                      <span className='flex items-center text-xs text-muted-foreground'>
                        <GripVertical
                          aria-hidden='true'
                          className='size-4'
                        />
                        Drag to reorder
                      </span>
                      <span className='ml-4'>Alias</span>
                      <span>Short Title</span>
                    </div>
                    <DroppableArea
                      container={container}
                      id='active'
                      items={symbols.active}
                    />
                  </>
                )}
                {isMaxCapacityReached && (
                  <>
                    <div className='relative my-6 flex items-center'>
                      <div className='h-0.5 flex-1 bg-[repeating-linear-gradient(to_right,var(--primary)_0_16px,transparent_16px_32px)]' />
                      <span className='absolute left-1/2 -translate-x-1/2 bg-card px-4'>
                        App display limit: {getAmountLabel(maxCapacity, itemsName)}
                      </span>
                    </div>

                    <DroppableArea
                      muted
                      baseIndex={symbols.active.length}
                      container={container}
                      id='backlog'
                      items={symbols.backlog}
                    />
                  </>
                )}
              </div>
            </div>
          </DragDropProvider>
        );
      }}
    </form.Subscribe>
  );
};

export default DragDropContainer;
